import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { getAdditionalUserInfo, type UserCredential } from 'firebase/auth';
import posthog from 'posthog-js';
import { getInsightsConnectionString } from 'src/config/authConfig';
import { auth } from 'src/auth/firebase';

declare global {
  interface Window {
    appInsights: ApplicationInsights;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const reactPlugin = new ReactPlugin();

export const appInsights = new ApplicationInsights({
  config: {
    connectionString: getInsightsConnectionString(),
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,
  },
});

appInsights.loadAppInsights();

// Get the existing instance
const getAppInsights = (): ApplicationInsights => {
  return appInsights;
};

// PostHog config is injected at build-time via webpack DefinePlugin
// (see frontend/webpack/webpack.common.js). Empty key → init is a no-op.
const POSTHOG_PROJECT_KEY = (process.env.POSTHOG_PROJECT_KEY as string) || '';
const POSTHOG_FE_API_HOST =
  (process.env.POSTHOG_FE_API_HOST as string) || 'https://us.i.posthog.com';
const POSTHOG_UI_HOST =
  (process.env.POSTHOG_UI_HOST as string) || 'https://us.posthog.com';

let posthogInitialized = false;

// Initialise PostHog. Called once from App.tsx on mount.
export const initPostHog = (): void => {
  if (posthogInitialized || !POSTHOG_PROJECT_KEY) return;
  try {
    posthog.init(POSTHOG_PROJECT_KEY, {
      api_host: POSTHOG_FE_API_HOST,
      // Required when api_host is a reverse proxy.
      ui_host: POSTHOG_UI_HOST,
      // Pins SDK behavioural defaults; bump alongside posthog-js upgrades.
      defaults: '2026-01-30',
      capture_pageview: 'history_change',
      // We emit explicit events; autocapture adds noise.
      autocapture: false,
      persistence: 'localStorage+cookie',
      person_profiles: 'identified_only',
      // Session recording's rrweb probes trip Firefox (esp. Responsive Design
      // Mode / iPhone simulation) with "Permission denied to access property
      // 'nodeType'" on input focus, which webpack surfaces as a red overlay.
      // Keep recording in production; local debugging doesn't need it.
      disable_session_recording: process.env.NODE_ENV === 'development',
    });
    posthogInitialized = true;
  } catch (error) {
    console.warn('Failed to init PostHog:', error);
  }
};

// Re-export so callers can call posthog.identify / posthog.reset directly.
export { posthog };

// PostHog super properties: attached to every subsequent event until
// unregistered. Pair with `unregisterSuperProperty` on unmount.
export const registerSuperProperties = (properties: {
  [key: string]: unknown;
}): void => {
  if (!posthogInitialized) return;
  try {
    posthog.register(properties);
  } catch (error) {
    console.warn('Failed to register PostHog super properties:', error);
  }
};

export const unregisterSuperProperty = (key: string): void => {
  if (!posthogInitialized) return;
  try {
    posthog.unregister(key);
  } catch (error) {
    console.warn(`Failed to unregister PostHog super property ${key}:`, error);
  }
};

// Project-page action types — kept narrow so the funnel breakdown stays
// readable. Add to this union when new project-page surfaces are added.
export type ProjectPageActionType = 'pdf' | 'xml';

// Funnel source-file kind. Keep in sync with BE `_kind_from_storage_file_name`.
export type UploadKind = 'xml' | 'image' | 'pdf';

/** Map ``storageFileName`` blob path → ``xml`` | ``pdf`` | ``image``. */
export const kindFromStorageFileName = (
  storageFileName?: string | null
): UploadKind => {
  const name = (storageFileName || '').toLowerCase();
  if (name.includes('/pages/')) return 'image';
  if (name.endsWith('.pdf')) return 'pdf';
  return 'xml';
};

// Google Ads "Upload Started" conversion (Goals → event snippet send_to).
const GOOGLE_ADS_UPLOAD_STARTED_SEND_TO = 'AW-16942780843/kiSKCOHVyeAcEKuj-Y4_';
// Google Ads "Sign-up" conversion (Goals → event snippet send_to).
const GOOGLE_ADS_SIGNUP_SEND_TO = 'AW-16942780843/jeNGCObt6O8cEKuj-Y4_';
const GOOGLE_ADS_SIGNUP_SENT_KEY = 'ga_signup_sent';

const pushDataLayerEvent = (
  eventName: string,
  properties?: { [key: string]: unknown }
): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...properties });
};

const gtag = (...args: unknown[]): void => {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

/** File picker and drag-and-drop both call this so Ads sees either gesture. */
export const reportGoogleAdsUploadStarted = (properties?: {
  [key: string]: unknown;
}): void => {
  pushDataLayerEvent('upload_started', properties);
  gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_UPLOAD_STARTED_SEND_TO,
    value: 1.0,
    currency: 'USD',
  });
};

/** Real account creation only. Session-deduped so email + OAuth cannot double-fire. */
export const reportGoogleAdsSignup = (properties?: {
  [key: string]: unknown;
}): void => {
  try {
    if (sessionStorage.getItem(GOOGLE_ADS_SIGNUP_SENT_KEY)) return;
    sessionStorage.setItem(GOOGLE_ADS_SIGNUP_SENT_KEY, '1');
  } catch {
    // Private mode / blocked storage — still fire this call.
  }
  pushDataLayerEvent('signup', properties);
  gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SIGNUP_SEND_TO,
    value: 1.0,
    currency: 'USD',
  });
};

/** Guest link or first-time create. Skips returning sign-in and existing-account takeover. */
export const reportGoogleAdsSignupIfNewAccount = (
  cred: UserCredential,
  properties?: { [key: string]: unknown }
): void => {
  const isNewAccount =
    cred.operationType === 'link' ||
    getAdditionalUserInfo(cred)?.isNewUser === true;
  if (!isNewAccount) return;
  reportGoogleAdsSignup(properties);
};

// Capture an explicit PostHog event. Additive to App Insights tracking.
export const captureFunnelEvent = (
  eventName: string,
  properties?: { [key: string]: unknown }
): void => {
  if (!posthogInitialized) return;
  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    console.warn(`Failed to capture PostHog event ${eventName}:`, error);
  }
};

// Emits `project_<action>` with ``type`` = pdf | xml.
export const trackProjectPageAction = (
  action: string,
  type: ProjectPageActionType,
  extras?: { [key: string]: unknown }
): void => {
  captureFunnelEvent(`project_${action}`, {
    type,
    ...extras,
  });
};

// Custom button click tracking
export const trackButtonClick = (
  pageName: string,
  buttonText: string,
  properties?: { [key: string]: unknown }
): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const eventName = `Button - ${pageName} - ${buttonText}`;
      const userId = auth.currentUser?.uid || 'not logged in';

      getAppInsights().trackEvent(
        { name: eventName },
        {
          ...properties,
          userId,
        }
      );

      // Flush the telemetry and resolve the promise
      getAppInsights().flush();
      resolve();
    } catch (error) {
      console.warn('Failed to track event:', error);
      resolve(); // Resolve even on error to not block the UI
    }
  });
};

// Custom page view tracking
export const trackPageView = (
  pageName: string,
  url: string,
  properties?: { [key: string]: unknown }
) => {
  try {
    const eventName = `PageView - ${pageName} - ${url}`;
    const userId = auth.currentUser?.uid || 'not logged in';

    getAppInsights().trackEvent(
      { name: eventName },
      {
        ...properties,
        userId,
      }
    );
  } catch (error) {
    console.warn('Failed to track event:', error);
  }
};

// App Insights + PostHog `$exception` (`handled: true`). Sinks are independent.
export const trackError = (
  error: Error,
  properties?: { [key: string]: unknown }
) => {
  try {
    getAppInsights().trackException({ error, properties });
  } catch (err) {
    console.warn('Failed to track error:', err);
  }

  if (!posthogInitialized) return;
  try {
    posthog.captureException(error, { handled: true, ...properties });
  } catch (err) {
    console.warn('Failed to capture PostHog exception:', err);
  }
};

export default getAppInsights;
